import { useQuery, useMutation } from "@apollo/client/react"
import LoadingButton from "@mui/lab/LoadingButton"
import Avatar from "@mui/material/Avatar"
import TextField from "@mui/material/TextField"
import { Check } from "lucide-mui"
import { DateTime } from "luxon"
import React, { useState } from "react"

import ActivitySkeleton from "@/components/activities/skeleton"
import Pagination from "@/components/pagination"
import { CREATE_CUSTOMER_NOTE_REFETCH_QUERIES } from "@/graphql/dependencies"
import { CreateCustomerNoteDocument, GetCustomerNotesDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import { useFlagWithDelay } from "@/shared/hooks"
import { showErrorToast } from "@/shared/toast"
import { useStore } from "@/store"

const Note = ({ customer: _customer, customerNote }) => {
  const defaultAvatar = "https://www.gravatar.com/avatar/?d=mp"

  const avatar = customerNote.user.avatarUrl || defaultAvatar
  const createdAt = DateTime.fromISO(customerNote.createdAt).toLocaleString(DateTime.DATETIME_MED)

  return (
    <div
      data-testid={`panel-clients-modal-notes-item-${customerNote.id}`}
      className="flex items-center space-x-4 px-6 py-2"
    >
      <Avatar className="" src={avatar} />

      <div className="flex-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500 uppercase">{customerNote.user.name}</span>
          <span className="text-xs text-gray-400">{createdAt}</span>
        </div>
        <div className="text-gray-800 py-1">{customerNote.note}</div>
      </div>
    </div>
  )
}

const NoteForm = ({ customer }) => {
  const [createNote, { loading }] = useMutation(CreateCustomerNoteDocument, {
    refetchQueries: CREATE_CUSTOMER_NOTE_REFETCH_QUERIES,
  })
  const { currentUser } = useStore()

  const [note, setNote] = useState("")
  const [success, setSuccess] = useFlagWithDelay()

  const handleChange = (event) => {
    setNote(event.target.value)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    try {
      await createNote({
        variables: {
          customerId: customer.id,
          note,
        },
      })

      setNote("")
      setSuccess(2000)
    } catch (error) {
      showErrorToast("Failed to save note. Please try again.")
      console.error(error)
    }
  }

  return (
    <form
      data-testid="panel-clients-modal-notes-form"
      onSubmit={handleSubmit}
      className="flex items-center space-x-4 p-6"
    >
      <Avatar src={currentUser.avatarUrl} />
      <TextField
        data-testid="panel-clients-modal-notes-input"
        className="flex-1"
        multiline
        placeholder={i18n.t("app.reservation_note.add_form.placeholder")}
        value={note}
        onChange={handleChange}
        maxRows={4}
        minRows={1}
      />
      <LoadingButton
        data-testid="panel-clients-modal-notes-submit"
        color={success ? "success" : "primary"}
        loadingPosition="center"
        loading={loading}
        type="submit"
      >
        {success ? <Check /> : i18n.t("app.reservation_note.add_form.submit")}
      </LoadingButton>
    </form>
  )
}

const NotesTab = ({ customer, customerContact: _customerContact }) => {
  const [page, setPage] = useState(1)

  const { data, loading, error } = useQuery(GetCustomerNotesDocument, {
    variables: {
      id: customer.id,
      page,
    },
  })

  const metadata = data?.business.customerNotes?.metadata
  const notes = data?.business.customerNotes?.collection || []

  if (!data && loading) {
    return (
      <div className="flex flex-col gap-4 p-2">
        <ActivitySkeleton />
      </div>
    )
  }

  if (error) return <div>Error: {error.message}</div>

  return (
    <div data-testid="panel-clients-modal-notes-container" className="flex flex-col pt-6 h-full">
      <div className="flex flex-1 flex-col space-y-3 items-stretch">
        {notes.map((note) => {
          return <Note key={note.id} customer={customer} customerNote={note} />
        })}
      </div>
      <div className="mt-auto">
        {metadata.totalPages > 0 && (
          <div data-testid="panel-clients-modal-notes-pagination" className="flex justify-center">
            <Pagination metadata={metadata} page={page} onPageChange={setPage} />
          </div>
        )}
        <NoteForm customer={customer} />
      </div>
    </div>
  )
}
export default NotesTab
